import { parse } from 'regexparam';

/**
 * Calculates the remaining path for a child router after a parent route has matched.
 * This function is designed to work with a routing system where parent routes are
 * explicitly marked with a trailing '/*'.
 *
 * @param pathFromParent - The full path segment the current router is working on (e.g., '/users/123/settings').
 * @param matchedRoutePattern - The path pattern from the route that was successfully matched (e.g., '/users/:id/*').
 * @returns The path segment remaining for the child router (e.g., '/settings'). Returns an empty string for terminal routes.
 */
export function calculatePathForChild(pathFromParent: string, matchedRoutePattern: string): string {
  const isParentRoute: boolean = matchedRoutePattern.endsWith('/*') || matchedRoutePattern === '*';

  if (!isParentRoute) {
    // For terminal routes (e.g., '/users/:id'), the entire path was consumed.
    // Nothing is left for a child router.
    return '';
  }

  // For parent routes (e.g., '/users/:id/*'), we determine what the pattern before '/*' matched.
  // It handles the edge case of a route path being just '/*'. `slice` would produce an empty string, but we want it to be '/'.
  const patternToMatch: string = matchedRoutePattern.slice(0, -2) || '/';

  // We need a regex that matches from the start of the string but is not anchored to the end.
  const { pattern }: { pattern: RegExp } = parse(patternToMatch);
  // Re-create the regex, ensuring it's only anchored at the start.
  // The pattern from regexparam might be anchored at both ends depending on the input.
  const regex: RegExp = new RegExp(`^${pattern.source.slice(1).replace(/\\?\/?\$$/, '')}`, pattern.flags);

  const match: RegExpExecArray | null = regex.exec(pathFromParent);

  if (!match) {
    // This case should not happen if this function is called after a successful match.
    // Returning the original path is a defensive fallback.
    return pathFromParent;
  }

  // match[0] contains the full string that was matched by the pattern, e.g., '/users/123'
  const consumedPart: string = match[0];

  // The path for the child is whatever is left over.
  const pathForChild: string = pathFromParent.substring(consumedPart.length);

  // Ensure the child path starts with a slash if it's not empty, which is a common convention.
  if (pathForChild.length > 0 && !pathForChild.startsWith('/')) {
    return `/${pathForChild}`;
  }

  // If a parent route matches the URL exactly (e.g., pattern '/*' and path '/'), the child should receive '/' as its base path.
  if (pathForChild.length === 0 && isParentRoute) {
    return '/';
  }

  return pathForChild;
}
